/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { call, delay, put, select, takeEvery } from "redux-saga/effects";

import { format, getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";

import { siteLocationPrefix } from "../../constants";
import { GameConceptIdSearchParam, ProgramSearchParam } from "../../helpers/dataFetchingHelpers";
import { CrmApplicationStatus, CurrentGameConcept } from "../../models/crmStatus";
import { OnboardingErrorType } from "../../models/errors";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { IdXboxFormsResponse } from "../../server/crm/api/models/forms";
import { PartnerCenterEnrollmentStatus } from "../../server/crm/dataService/models/staticOptions";
import { CrmIdXboxActions, crmIdXboxActions } from "../actions/crmApiActions";
import { programsActions } from "../actions/programsActions";
import {
  getCrmApplicationStatus,
  getCurrentGameConcept,
  getPartnerCenterEnrollmentStatus,
} from "../selectors";
import { storeError } from "./dataStorage/storeError";

export const crmGetDataUrl = `${siteLocationPrefix}/forms/data`;
export const crmGetDataUrlWithGameConceptId = `${siteLocationPrefix}/forms/data?${ProgramSearchParam}=${Programs.IdXbox}&${GameConceptIdSearchParam}={0}`;

// Setting the polling delay as 1min since we don't want to trigger a lot of requests to CRM/Contracts API
export const pollingDelay = 30000;

// This is hardcoded for ID@Xbox, if a new program were to use game concepts in the future. We need to make this whole saga generic.
export function* fetchIdXboxStatusUpdatesStart(
  action: ReturnType<typeof crmIdXboxActions.fetchIdXboxStatusUpdates>
) {
  const isSignedIn: boolean = yield select(getIsUserSignedIn);
  if (!isSignedIn) {
    return;
  }

  try {
    let ndaNotSignedAndValid = true;

    while (ndaNotSignedAndValid) {
      yield delay(pollingDelay);

      const currentStatus: CrmApplicationStatus = yield select(getCrmApplicationStatus);
      let ndaStatus: CrmApplicationStatus = currentStatus;

      const currentGameConcept: CurrentGameConcept | undefined =
        yield select(getCurrentGameConcept);

      const response: Response = yield call(
        fetch,
        format(crmGetDataUrlWithGameConceptId, currentGameConcept?.id as string)
      );
      const data: IdXboxFormsResponse = yield call([response, "json"]);
      if (!data) {
        throw new Error("Forms data is undefined");
      }

      // Get NDA status from the forms response
      ndaStatus = data.crmStatus.crmApplicationStatus;
      ndaNotSignedAndValid =
        ndaStatus !== CrmApplicationStatus.NdaSigned && ndaStatus !== CrmApplicationStatus.Rejected;

      if (!ndaNotSignedAndValid) {
        action.history.push(OnboardingUrls[RouteNames.IdXboxWelcome]);
      }

      if (data) {
        yield put(programsActions.addProgramData(Programs.IdXbox, data));
      }
    }
  } catch (e) {
    yield storeError(e, OnboardingErrorType.StatusPolling);
  }
}

export const setEnrollmentInProgressPollingMaxTries = (newValue: number) => {
  enrollmentInProgressPollingMaxTries = newValue;
};

let enrollmentInProgressPollingMaxTries = 10;

export function* fetchDataForPartnerCenterEnrollmentPolling(
  _action: ReturnType<typeof crmIdXboxActions.fetchDataForPartnerCenterEnrollmentPolling.start>
) {
  const isSignedIn: boolean = yield select(getIsUserSignedIn);
  if (!isSignedIn) {
    return;
  }

  let partnerCenterEnrollmentStatus: PartnerCenterEnrollmentStatus = yield select(
    getPartnerCenterEnrollmentStatus
  );
  let tryNumber = 1;

  while (
    partnerCenterEnrollmentStatus === PartnerCenterEnrollmentStatus.InProgress &&
    tryNumber < enrollmentInProgressPollingMaxTries
  ) {
    tryNumber++;
    yield delay(pollingDelay);

    try {
      const response: Response = yield call(fetch, crmGetDataUrl);
      const data: IdXboxFormsResponse = yield call([response, "json"]);

      if (!data) {
        throw new Error("Forms data is undefined");
      }

      yield put(programsActions.addProgramData(Programs.IdXbox, data));
    } catch (e) {
      yield storeError(e, OnboardingErrorType.StatusPolling);
    }

    partnerCenterEnrollmentStatus = yield select(getPartnerCenterEnrollmentStatus);
  }
}

export function* crmIdXboxSagas() {
  yield takeEvery(CrmIdXboxActions.FETCH_STATUS_UPDATES, fetchIdXboxStatusUpdatesStart);
  yield takeEvery(
    CrmIdXboxActions.FETCH_DATA_PARTNER_CENTER_ENROLLMENT_POLLING_START,
    fetchDataForPartnerCenterEnrollmentPolling
  );
}
